import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

export const TextReveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={isInView ? { y: 0, opacity: 1 } : { y: '100%', opacity: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
      >
        {children}
      </motion.div>
    </div>
  );
};

export const StaggerReveal = ({ children, className = '', staggerDelay = 0.08 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: staggerDelay } },
      }}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ children, className = '' }) => (
  <motion.div
    className={className}
    variants={{
      hidden: { opacity: 0, y: 24 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
    }}
  >
    {children}
  </motion.div>
);

export const ParallaxImage = ({ src, alt, className = '', speed = 0.15 }) => {
  const ref = useRef(null);

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        className="w-full h-[115%] object-cover"
        initial={{ y: '-5%' }}
        whileInView={{ y: '0%' }}
        viewport={{ once: false, amount: 0 }}
        transition={{ duration: 0 }}
        style={{ willChange: 'transform' }}
        onViewportEnter={() => {
          const el = ref.current;
          if (!el) return;
          const img = el.querySelector('img');
          const handleScroll = () => {
            const rect = el.getBoundingClientRect();
            const viewH = window.innerHeight;
            const progress = (viewH - rect.top) / (viewH + rect.height);
            const offset = (progress - 0.5) * speed * 100;
            if (img) img.style.transform = `translateY(${offset}px)`;
          };
          window.addEventListener('scroll', handleScroll, { passive: true });
          el._cleanup = () => window.removeEventListener('scroll', handleScroll);
          handleScroll();
        }}
        onViewportLeave={() => {
          const el = ref.current;
          if (el && el._cleanup) el._cleanup();
        }}
      />
    </div>
  );
};
